import {
  Ruler3DType,
  RulerLineType,
  RoomType,
  SymbolType,
  WallType,
} from "../../../types/floorplannerTypes";

export type FloorPlan3DFocusScope =
  | { kind: "canvas" }
  | { kind: "room"; roomId: string }
  | { kind: "objectSet"; objectIds: string[] };

export type FloorPlan3DScopeStore = {
  wallsMap: Map<string, WallType>;
  roomsMap: Map<string, RoomType>;
  symbolsMap: Map<string, SymbolType>;
  rulerLinesMap?: Map<string, RulerLineType>;
  rulers3DMap?: Map<string, Ruler3DType>;
};

export type FloorPlan3DScopeData = {
  scope: FloorPlan3DFocusScope;
  walls: WallType[];
  rooms: RoomType[];
  symbols: SymbolType[];
  rulerLines: RulerLineType[];
  rulers3D: Ruler3DType[];
  isWholeCanvas: boolean;
};

export function resolveFloorPlan3DScope(
  store: FloorPlan3DScopeStore,
  scope: FloorPlan3DFocusScope = { kind: "canvas" },
): FloorPlan3DScopeData {
  if (scope.kind === "canvas") {
    return {
      scope,
      walls: Array.from(store.wallsMap.values()),
      rooms: Array.from(store.roomsMap.values()),
      symbols: Array.from(store.symbolsMap.values()),
      rulerLines: Array.from(store.rulerLinesMap?.values() ?? []),
      rulers3D: Array.from(store.rulers3DMap?.values() ?? []),
      isWholeCanvas: true,
    };
  }

  if (scope.kind === "room") {
    const room = store.roomsMap.get(scope.roomId);
    if (!room) {
      return {
        scope,
        walls: [],
        rooms: [],
        symbols: [],
        rulerLines: [],
        rulers3D: [],
        isWholeCanvas: false,
      };
    }

    const roomObjectIds = new Set(room.objectIds);
    const walls = Array.from(store.wallsMap.values()).filter(
      (wall) => roomObjectIds.has(wall.id) || wall.roomIds?.includes(room.id),
    );
    const resolvedWallIds = new Set(walls.map((wall) => wall.id));
    const symbols = Array.from(store.symbolsMap.values()).filter((symbol) => {
      return (
        resolvedWallIds.has(symbol.attachedTo ?? "") ||
        wallOwnsAttachment(resolvedWallIds, walls, symbol.id)
      );
    });

    return {
      scope,
      walls,
      rooms: [room],
      symbols,
      rulerLines: Array.from(store.rulerLinesMap?.values() ?? []).filter(
        (rulerLine) => roomObjectIds.has(rulerLine.id),
      ),
      rulers3D: Array.from(store.rulers3DMap?.values() ?? []).filter(
        (ruler) => roomObjectIds.has(ruler.id),
      ),
      isWholeCanvas: false,
    };
  }

  const objectIds = new Set(scope.objectIds);
  const walls = Array.from(store.wallsMap.values()).filter((wall) =>
    objectIds.has(wall.id),
  );
  const wallIds = new Set(walls.map((wall) => wall.id));
  const symbols = Array.from(store.symbolsMap.values()).filter(
    (symbol) =>
      objectIds.has(symbol.id) ||
      wallIds.has(symbol.attachedTo ?? "") ||
      wallOwnsAttachment(wallIds, walls, symbol.id),
  );
  const rooms = Array.from(store.roomsMap.values()).filter((room) =>
    room.objectIds.some((objectId) => objectIds.has(objectId)),
  );

  return {
    scope,
    walls,
    rooms,
    symbols,
    rulerLines: Array.from(store.rulerLinesMap?.values() ?? []).filter(
      (rulerLine) => objectIds.has(rulerLine.id),
    ),
    rulers3D: Array.from(store.rulers3DMap?.values() ?? []).filter(
      (ruler) => objectIds.has(ruler.id),
    ),
    isWholeCanvas: false,
  };
}

function wallOwnsAttachment(
  wallIds: Set<string>,
  walls: WallType[],
  symbolId: string,
): boolean {
  return walls.some(
    (wall) =>
      wallIds.has(wall.id) &&
      wall.symbolAttachments?.some(
        (attachment) => attachment.symbolId === symbolId,
      ),
  );
}
